import { AssignUsers, invalidateAccessControlQueries, toast, UpdateUsers } from '@lovoldsystem/authorization.client'
import { AddCircleOutline, Close, CloseOutlined, PersonRemove, Search, Send } from '@mui/icons-material'
import { Autocomplete, Box, Chip, InputAdornment, styled, Typography, useTheme } from '@mui/material'
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete'
import { t } from 'i18next'
import debounce from 'lodash/debounce'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { invalidateAccessGroups, useLocalities } from 'src/api/access-group'
import { useGetCustomerLocalitiesWithGroups } from 'src/api/localities'
import { useGetAllServiceStationsWithGroups } from 'src/api/service-station'
import { useGetStorageForDropDown } from 'src/api/storage'
import { invalidateUserInviteCache, useBulkUserInvite } from 'src/api/user-invites'
import { AppTextField } from 'src/components/AppTextField'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { UserStatus } from 'src/enums/user-status'
import { IUser } from 'src/generated-types'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { coreUtils, getThemeColor } from 'src/utils'
import {
  invalidateCustomer,
  useDeleteAccessBulk,
  useGetCustomerUsersForAdmins,
  useGetUserRoles,
} from '../../../api/customer'
import { permissionBinding, permissionLevel } from '../../../constant'
import { FacilityTypes, RoleTypes } from '../../../enums'
import { FunctionElementsParentsIds } from '../../../enums/auth'
import { getCustomerUsers } from '../../../http'
import { UserTable } from './UserTable'
import { IRole } from '@lovoldsystem/authorization.client/types/role'
import { GridActionButton } from 'src/components/common/GridActionButton'
// import { useGetProjectsForDropDown } from 'src/api/project'

export const UsersContainer: FC<{ isAdminLayout?: boolean; isGlobalServiceProvider?: boolean }> = ({
  isAdminLayout,
  isGlobalServiceProvider,
}) => {
  const { t: translate } = useTranslation(['users', 'common'])
  const { id } = useParams()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const theme = useTheme()
  const location = useLocation()
  const [searchText, setSearchText] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([])
  const [roleFilters, setRoleFilters] = useState<IRole[]>([])
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false)
  const [roleSearchText, setRoleSearchText] = useState('')
  const { mutateAsync: inviteUsers, isLoading } = useBulkUserInvite(id as EntityId)
  const { data } = useGetCustomerUsersForAdmins(id as EntityId, true, searchText)
  const { mutateAsync: deleteBulkAsync } = useDeleteAccessBulk(id as EntityId)
  const { data: userRoles } = useGetUserRoles(id as EntityId)

  const roles = useMemo(() => {
    if (!userRoles) return []
    return [
      ...userRoles,
      {
        id: -1,
        name: translate('direct-permissions', { ns: 'access-control' }),
      },
    ]
  }, [userRoles, translate])

  const handleNewUserAdd = () => {
    showDialog(
      AssignUsers,
      {
        title: translate('add-client-users', { ns: 'users' }),
        componentProps: {
          clientId: id as any,
          getUsersAction: getCustomerUsers,
          roleTypes:
            isGlobalServiceProvider || (location?.state as object)?.['isGlobalServiceProvider']
              ? [RoleTypes.ServiceStation, RoleTypes.Client]
              : [RoleTypes.Client],
          assignCompleted: users => {
            invalidateCustomer.getCustomerUsersForAdmins(id as EntityId, true)
            invalidateAccessGroups.getLocalityUsers(id as EntityId, undefined)
            invalidateAccessGroups.getStorageUsers(id as EntityId, undefined)
            invalidateAccessGroups.getServiceStationUsers(id as EntityId, undefined)
            users?.forEach(user => {
              invalidateAccessControlQueries.invalidateGetDirectPermissions(parseInt(id as string), user.id)
            })
            invalidateCustomer.getCustomersWithAdminUserPermission()
          },
          permissionLevel: permissionLevel,
          permissionBinding: permissionBinding,
          useGetAssignedTo: useLocalities,
          adminParentIds: [FunctionElementsParentsIds.SystemPortal],
          assignToAlert: t('permission-types', { ns: 'user-resources' }),
          readOnly: true,
          existingUsers: data,
          theme: theme,
          initialValues: {},
          functionElementParents:
            isGlobalServiceProvider || (location?.state as object)?.['isGlobalServiceProvider']
              ? [FunctionElementsParentsIds.ServiceStationPortal]
              : [
                  FunctionElementsParentsIds.LocalityPortal,
                  FunctionElementsParentsIds.StoragePortal,
                  FunctionElementsParentsIds.ServiceStationPortal,
                  FunctionElementsParentsIds.SystemPortal,
                  // FunctionElementsParentsIds.ProjectPlanningPortal,
                ],
          assignedTo: [
            {
              assignToText: t('localities', { ns: 'user-resources' }),
              assignToAlert: t('permission-types', { ns: 'user-resources' }),
              useAssignedTo: useGetCustomerLocalitiesWithGroups,
              key: FacilityTypes.locality,
              functionElementsParentsId: FunctionElementsParentsIds.LocalityPortal,
            },
            {
              assignToText: t('service-stations', { ns: 'user-resources' }),
              assignToAlert: t('permission-types', { ns: 'user-resources' }),
              useAssignedTo: useGetAllServiceStationsWithGroups,
              key: FacilityTypes.serviceStation,
              functionElementsParentsId: FunctionElementsParentsIds.ServiceStationPortal,
            },
            {
              assignToText: t('storages', { ns: 'storage' }),
              assignToAlert: t('permission-types', { ns: 'user-resources' }),
              useAssignedTo: useGetStorageForDropDown,
              key: FacilityTypes.storage,
              functionElementsParentsId: FunctionElementsParentsIds.StoragePortal,
            },
            // {
            //   assignToText: t('projects', { ns: 'projects' }),
            //   assignToAlert: t('permission-types', { ns: 'user-resources' }),
            //   useAssignedTo: useGetProjectsForDropDown,
            //   key: FacilityTypes.ProjectPlanning,
            //   functionElementsParentsId: FunctionElementsParentsIds.ProjectPlanningPortal,
            // },
          ],
          facilityTypes: FacilityTypes,
          isNonMaskedEmail: true,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
      },
      undefined,
    )
  }

  const handleUpdateUserAdd = (clientUserLinks, user, services, initialDirectPermissions) => {
    showDialog(
      UpdateUsers,
      {
        title: translate('update-client-users', { ns: 'users' }),
        componentProps: {
          clientUserLinks: clientUserLinks,
          user: user,
          services,
          clientId: id as any,
          getUsersAction: getCustomerUsers,
          initialDirectPermissions,
          roleTypes:
            isGlobalServiceProvider || (location?.state as object)?.['isGlobalServiceProvider']
              ? [RoleTypes.ServiceStation, RoleTypes.Client]
              : [RoleTypes.Client],
          assignCompleted: users => {
            invalidateCustomer.getCustomerUsersForAdmins(id as EntityId, true)
            invalidateAccessGroups.getLocalityUsers(id as EntityId, undefined)
            invalidateAccessGroups.getStorageUsers(id as EntityId, undefined)
            invalidateAccessGroups.getServiceStationUsers(id as EntityId, undefined)
            users?.forEach(user => {
              invalidateAccessControlQueries.invalidateGetDirectPermissions(parseInt(id as string), user.id)
            })
            invalidateCustomer.getCustomersWithAdminUserPermission()
          },
          permissionLevel: permissionLevel,
          adminParentIds: [FunctionElementsParentsIds.SystemPortal],
          permissionBinding: permissionBinding,
          useGetAssignedTo: useLocalities,
          assignToAlert: t('permission-types', { ns: 'user-resources' }),
          readOnly: true,
          theme: theme,
          functionElementParents:
            isGlobalServiceProvider || (location?.state as object)?.['isGlobalServiceProvider']
              ? [FunctionElementsParentsIds.ServiceStationPortal]
              : [
                  FunctionElementsParentsIds.LocalityPortal,
                  FunctionElementsParentsIds.StoragePortal,
                  FunctionElementsParentsIds.ServiceStationPortal,
                  FunctionElementsParentsIds.SystemPortal,
                  // FunctionElementsParentsIds.ProjectPlanningPortal,
                ],
          assignedTo: [
            {
              assignToText: t('localities', { ns: 'user-resources' }),
              assignToAlert: t('permission-types', { ns: 'user-resources' }),
              useAssignedTo: useGetCustomerLocalitiesWithGroups,
              key: FacilityTypes.locality,
              functionElementsParentsId: FunctionElementsParentsIds.LocalityPortal,
            },
            {
              assignToText: t('service-stations', { ns: 'user-resources' }),
              assignToAlert: t('permission-types', { ns: 'user-resources' }),
              useAssignedTo: useGetAllServiceStationsWithGroups,
              key: FacilityTypes.serviceStation,
              functionElementsParentsId: FunctionElementsParentsIds.ServiceStationPortal,
            },
            {
              assignToText: t('storages', { ns: 'storage' }),
              assignToAlert: t('permission-types', { ns: 'user-resources' }),
              useAssignedTo: useGetStorageForDropDown,
              key: FacilityTypes.storage,
              functionElementsParentsId: FunctionElementsParentsIds.StoragePortal,
            },
            // {
            //   assignToText: t('projects', { ns: 'projects' }),
            //   assignToAlert: t('permission-types', { ns: 'user-resources' }),
            //   useAssignedTo: useGetProjectsForDropDown,
            //   key: FacilityTypes.ProjectPlanning,
            //   functionElementsParentsId: FunctionElementsParentsIds.ProjectPlanningPortal,
            // },
          ],
          facilityTypes: FacilityTypes,
          isNonMaskedEmail: true,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
      },
      undefined,
    )
  }

  const handleBulkInvite = useCallback(
    (selectedUsers: IUser[]) => {
      showConfirmDialog(
        t('new-invite', { ns: 'user-invite' }),
        <>
          <Typography>{t('new-invite-message', { ns: 'user-invite' })}</Typography>
          <Flex.Row gap={1} marginTop={2} flexWrap={'wrap'}>
            {selectedUsers.map(user => (
              <Chip
                key={user.id}
                sx={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  bgcolor: getThemeColor(theme, MUIThemeColors.primaryMain),
                  color: getThemeColor(theme, MUIThemeColors.white),
                }}
                label={user.email}
              />
            ))}
          </Flex.Row>
        </>,
        {
          acceptText: t('yes', { ns: 'common' }),
          cancelText: t('no', { ns: 'common' }),
        },
        async pop => {
          const selectedInviteUsers = selectedUsers.map(user => user.email)
          inviteUsers(
            { emails: selectedInviteUsers },
            {
              onSuccess: () => {
                invalidateUserInviteCache.useGetUserInvites(id as EntityId)
                invalidateCustomer.getCustomerUsersForAdmins(id as EntityId, true)
                setSelectedUsers([])
                toast.success(t('successfullyInvited', { ns: 'common-api-message' }))
              },
              onError: error => {
                toast.error(t('unExpectedError', { ns: 'common-api-message' }))
              },
            },
          )
          pop()
        },
      )
    },
    [id],
  )

  const handleSelectCheckbox = useCallback((e, row, data) => {
    const filteredData = [...data]
    setSelectedUsers((prev: IUser[]) => {
      return coreUtils.handleRowSelectFunc<IUser>(prev, row, filteredData || [], e)
    })
  }, [])

  const handleDeleteAccessBulk = useCallback(selectedUsers => {
    showConfirmDialog(
      translate('remove-access', { ns: 'access-control' }),
      translate('remove-access-description', { ns: 'access-control' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        await deleteBulkAsync(
          selectedUsers.map(u => u.id),
          {
            onSuccess: () => {
              invalidateCustomer.getCustomerUsersForAdmins(id as EntityId, true)
              invalidateAccessGroups.getLocalityUsers(id as EntityId, undefined)
              invalidateAccessGroups.getStorageUsers(id as EntityId, undefined)
              invalidateAccessGroups.getServiceStationUsers(id as EntityId, undefined)
              setSelectedUsers([])
              toast.success(translate('deletedSuccessfully', { ns: 'common' }))
              pop()
            },
            onError: error => {
              toast.error(translate('deletingError', { ns: 'common' }))
            },
          },
        )
      },
    )
  }, [])

  const debouncedSearch = useCallback(
    debounce(nextValue => setSearchText(nextValue?.trim() || ''), 400),
    [],
  )

  const handleChange = event => {
    const value = event.target.value
    debouncedSearch(value)
  }

  const roleSelectLabel =
    roleFilters.length > 0
      ? translate('role-selected', { count: roleFilters.length, ns: 'users' })
      : translate('role-filter', { ns: 'users' })

  const handleRoleDropdownToggle = useCallback(() => setRoleDropdownOpen(prev => !prev), [])
  const handleRoleDropdownOpen = useCallback(() => setRoleDropdownOpen(true), [])
  const handleRoleDropdownClose = useCallback(() => {
    setRoleDropdownOpen(false)
    setRoleSearchText('')
  }, [])

  const renderRoleSelectInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <AppTextField
        {...params}
        sx={{ p: 0, width: '100%' }}
        inputProps={{
          ...params.inputProps,
          readOnly: !roleDropdownOpen,
        }}
        InputProps={{
          ...params.InputProps,
          ...(!roleDropdownOpen && {
            onMouseDown: (e: React.MouseEvent) => {
              if ((e.target as HTMLElement).closest('.MuiAutocomplete-popupIndicator')) return
              e.preventDefault()
              handleRoleDropdownToggle()
            },
          }),
          startAdornment: !roleDropdownOpen ? (
            <Typography
              component="span"
              sx={{ fontSize: 14, fontWeight: 400, pointerEvents: 'none', width: '100%' }}
              className="role-select-label"
            >
              {roleSelectLabel}
            </Typography>
          ) : undefined,
        }}
        fullWidth
      />
    ),
    [roleSelectLabel, handleRoleDropdownToggle, roleDropdownOpen],
  )

  return (
    <CommonPageLayout
      titleSection={{
        title: translate('users', { ns: 'users' }),
      }}
      topAction={[
        <StyledSearchField key={'search'}>
          <AppTextField
            label={translate('search', { ns: 'common' })}
            placeholder={translate('search', { ns: 'common' })}
            fullWidth
            onChange={evt => handleChange(evt)}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            key={'search'}
          />
        </StyledSearchField>,
        <StyledSelectField
          multiple
          options={roles ?? []}
          value={roleFilters}
          onChange={(_, value) => setRoleFilters((value as IRole[]) ?? [])}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderTags={() => null}
          inputValue={roleSearchText}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setRoleSearchText(value)
          }}
          disableCloseOnSelect
          open={roleDropdownOpen}
          onOpen={handleRoleDropdownOpen}
          onClose={handleRoleDropdownClose}
          renderInput={renderRoleSelectInput}
          ListboxProps={{
            style: {
              backgroundColor: `${getThemeColor(theme, MUIThemeColors.white)}`,
              maxHeight: 300,
              overflow: 'auto',
            },
          }}
          componentsProps={{
            paper: {
              sx: {
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: `${getThemeColor(theme, MUIThemeColors.secondaryLight)} !important`,
                },
              },
            },
          }}
          key={'role-filter'}
        />,
        {
          buttonText: translate('invite', { ns: 'user-invite' }),
          buttonIcon: <Send sx={{ fontSize: 24 }} />,
          loading: isLoading,
          onClick: () =>
            handleBulkInvite(
              selectedUsers.filter(u => u.status == UserStatus.Pending || u.isWelcomeEmailSent == false),
            ),
          disabled: !selectedUsers.some(u => u.status == UserStatus.Pending || u.isWelcomeEmailSent == false),
          variant: 'outlined',
          shadowWeight: null,
          toolTipTitle: selectedUsers.length === 0 ? translate('selectUsers', { ns: 'users' }) : undefined,
          border: 1,
        },
        {
          buttonText: translate('delete', { ns: 'common' }),
          buttonIcon: <PersonRemove sx={{ fontSize: 24 }} />,
          loading: isLoading,
          onClick: () => handleDeleteAccessBulk(selectedUsers),
          disabled: selectedUsers.length === 0,
          variant: 'outlined',
          shadowWeight: null,
          toolTipTitle: selectedUsers.length === 0 ? translate('selectUsers', { ns: 'users' }) : undefined,
          border: 1,
        },
        {
          buttonText: translate('addUser', { ns: 'users' }),
          buttonIcon: <AddCircleOutline />,
          onClick: handleNewUserAdd,
          variant: 'outlined',
          shadowWeight: null,
          border: 1,
        },
      ]}
      enableBackButton={isAdminLayout}
    >
      {roleFilters && roleFilters.length > 0 && (
        <Box
          paddingBottom={'8px'}
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          gap={1}
          flexWrap={'wrap'}
          minWidth={0}
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            gap={1}
            flexWrap={'wrap'}
            flex={1}
            minWidth={0}
            overflow={'hidden'}
          >
            {roleFilters?.map(role => (
              <Chip
                key={role.id}
                label={role.id === -1 ? translate('direct-permissions', { ns: 'access-control' }) : role.name}
                onDelete={() => setRoleFilters(roleFilters.filter(r => r.id !== role.id))}
                deleteIcon={<Close />}
              />
            ))}
          </Box>
          <StyledTextButton
            size="small"
            onClick={() => setRoleFilters([])}
            sx={{ ml: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}
            buttonIcon={<CloseOutlined />}
            toolTipTitle={translate('clear-all-filters', { ns: 'users' })}
          />
        </Box>
      )}
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <UserTable
          customerId={id as EntityId}
          onRowClick={(id, user, services, directPermissions) =>
            handleUpdateUserAdd(id, user, services, directPermissions)
          }
          handleSelectCheckbox={handleSelectCheckbox}
          selectedUsers={selectedUsers}
          searchText={searchText}
          roleFilters={roleFilters}
        />
      </Box>
    </CommonPageLayout>
  )
}

const StyledTextButton = styled(GridActionButton)`
  background-color: transparent !important;
  box-shadow: none;
  border: none;
  &:hover {
    background-color: transparent !important;
  }
`

export const StyledSearchField = styled(Box)(({ theme }) => ({
  '& .MuiBox-root': {
    padding: '0 !important',
  },
  '& .MuiInputBase-root': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
    height: '36px',
    width: '300px',
  },
  '& .MuiInputLabel-root': {
    transform: 'translate(14px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
}))

const StyledSelectField = styled(Autocomplete)<any>(({ theme, open }) => ({
  width: '300px',
  '& .MuiBox-root': {
    padding: '0 !important',
  },
  '& .MuiAutocomplete-inputRoot': {
    padding: '0 32px 0 8px !important',
    flexWrap: 'nowrap',
  },
  '& .MuiAutocomplete-input': {
    ...(open ? {} : { opacity: 0, width: '0 !important', minWidth: '0 !important', padding: '0 !important' }),
  },
  '& .role-select-label': {
    color: getThemeColor(theme, MUIThemeColors.black),
    whiteSpace: 'nowrap',
  },
  '& .MuiInputBase-root': {
    height: '36px',
    width: '100%',
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  },
  '& .MuiAutocomplete-tag': {
    display: 'none',
  },
  '& .MuiAutocomplete-clearIndicator': {
    display: 'none',
  },
}))
